import React, { useEffect } from "react";
import { useHttpRequest, LoadingState } from "../http";
import { GetTeamChildrenResponseDto } from "../common/model/get-team-children.dto";
import { TeamDtoResponse, TeamOwnerDto } from "../common/model/team.dto";
import { TeamOwnerDetailsResponseDto } from "../common/model/teamownerdetails.dto";
import DeleteTeamModal, { DeleteTeamProps } from "./DeleteTeamModal";
import { MetricsState } from "../logging/metricsSlice";
import { TeamListItem } from "./TeamList";
import { DisplayBanner } from "../banner/bannerSlice";
import { GetSubheaderDetailFetchPayload } from "../layout/subheader/subheaderSlice";

export interface DeleteTeamWrapperProps {
  metrics: MetricsState;
  setShowModal: (visible: boolean) => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) => void;
}

function getOwnerIdsFromTeams(
  teamChildrenResponse?: GetTeamChildrenResponseDto
): Set<string> {
  if (!teamChildrenResponse) {
    return new Set([]);
  }

  const ownerIds = teamChildrenResponse.children
    .filter((childTeam) => {
      return childTeam.owner;
    })
    .map((childTeam) => {
      return childTeam.owner!.employeeId;
    });
  return new Set(ownerIds);
}

const DeleteTeamWrapper: React.FC<DeleteTeamProps & DeleteTeamWrapperProps> = ({
  teamId,
  teamName,
  parentTeamId,
  showModal,
  setShowModal,
  metrics,
  setBanner,
  getSubheaderDetailFetch,
}): JSX.Element => {
  const {
    loadingState: teamChildrenLoadingState,
    data: teamChildren,
    sendRequest: sendGetChildrenRequest,
  } = useHttpRequest<GetTeamChildrenResponseDto>("getTeamChildren", metrics);

  const {
    loadingState: getTeamInfoLoadingState,
    data: teamInfo,
    sendRequest: sendGetTeamInfo,
  } = useHttpRequest<TeamDtoResponse>("getTeamInfo", metrics);

  const {
    loadingState: teamsOwnersLoading,
    data: teamsOwnersDetails,
    sendRequest: sendGetTeamsOwners,
  } = useHttpRequest<TeamOwnerDetailsResponseDto>("getTeamsOwners", metrics);

  const {
    loadingState: getParentTeamLoadingState,
    data: parentTeamInfo,
    sendRequest: sendGetParentTeamInfo,
  } = useHttpRequest<TeamDtoResponse>("getTeamInfo", metrics);

  const teamHasChildren: boolean = teamChildren
    ? teamChildren.children.length > 0
    : false;

  const additionalInfoLoaded: boolean =
    getTeamInfoLoadingState === LoadingState.RESOLVED &&
    getParentTeamLoadingState === LoadingState.RESOLVED &&
    teamsOwnersLoading === LoadingState.RESOLVED;

  useEffect(() => {
    if (showModal) {
      sendGetChildrenRequest({
        url: `/api/v1/teams/${teamId}/children`,
        method: "GET",
      });
    }
  }, [sendGetChildrenRequest, showModal, teamId]);

  //TODO: find a more efficient way to do this.
  useEffect(() => {
    if (teamChildrenLoadingState !== LoadingState.RESOLVED) {
      return;
    }
    if (!teamHasChildren) {
      return;
    }
    sendGetTeamInfo({
      url: `/api/v1/teams/${teamId}`,
      method: "GET",
    });
    sendGetTeamsOwners({
      url: `/api/v1/teams/query/employees`,
      method: "POST",
      data: {
        employeeIds: [...getOwnerIdsFromTeams(teamChildren)],
      },
    });
  }, [sendGetTeamInfo, teamChildrenLoadingState, teamChildren]);

  useEffect(() => {
    if (getTeamInfoLoadingState === LoadingState.RESOLVED) {
      if (teamInfo && teamInfo.data.reportsTo) {
        sendGetParentTeamInfo({
          url: `/api/v1/teams/${teamInfo.data.reportsTo}`,
          method: "GET",
        });
      }
    }
  }, [sendGetParentTeamInfo, getTeamInfoLoadingState, teamInfo]);

  const parentTeamName = parentTeamInfo ? parentTeamInfo.data.name : "";

  const ownersMap = new Map<string, string>(
    teamsOwnersDetails
      ? teamsOwnersDetails.data.map((owner) => [
          owner.employeeId,
          owner.employeeFullName,
        ])
      : []
  );

  const resolveOwnerName = (owner?: TeamOwnerDto): string => {
    if (!owner) {
      return "No owner assigned";
    }
    let ownerName = ownersMap.get(owner.employeeId);
    if (!ownerName) {
      return "No owner assigned";
    }
    return `Owned by ${ownerName}`;
  };

  let teamListItems: TeamListItem[] = [];
  if (teamChildrenLoadingState === LoadingState.RESOLVED && teamChildren) {
    teamListItems = teamChildren.children.map((team) => {
      return {
        name: team.name,
        description: resolveOwnerName(team.owner),
      };
    });
  }

  let isFinishedLoading: boolean = false;
  if (teamChildrenLoadingState === LoadingState.RESOLVED) {
    if (teamHasChildren) {
      isFinishedLoading = additionalInfoLoaded;
    } else {
      isFinishedLoading = true;
    }
  }

  return (
    <DeleteTeamModal
      showModal
      setShowModal={setShowModal}
      teamId={teamId}
      teamName={teamName}
      parentTeamId={parentTeamId}
      teamChildren={teamListItems}
      parentTeamName={parentTeamName}
      isLoading={!isFinishedLoading}
      setBanner={setBanner}
      getSubheaderDetailFetch={getSubheaderDetailFetch}
      metrics={metrics}
    />
  );
};

export default DeleteTeamWrapper;
